import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useCustomer } from "../context/CustomerContext";
import { formatApiError } from "../lib/api";

export default function CustomerAuthPage() {
  const [params, setParams] = useSearchParams();
  const mode = params.get("mode") === "signup" ? "signup" : "login";
  const { login, register } = useCustomer();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const customer =
        mode === "signup"
          ? await register(form.name, form.email, form.password)
          : await login(form.email, form.password);
      toast.success(`Welcome${mode === "signup" ? " aboard" : " back"}, ${customer.name.split(" ")[0]}!`);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err, "Authentication failed"));
    }
    setLoading(false);
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-16" data-testid="customer-auth-page">
      <div className="card-surface w-full max-w-sm p-8">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-400/15 text-amber-300">
          <UserRound size={22} />
        </span>
        <div className="mt-5 flex rounded-full border border-white/10 p-1">
          {[["login", "Log in"], ["signup", "Sign up"]].map(([m, label]) => (
            <button
              key={m}
              data-testid={`auth-tab-${m}`}
              onClick={() => setParams({ mode: m })}
              className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${
                mode === m ? "bg-amber-400 text-black" : "text-slate-400 hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="mt-4 text-center text-xs text-slate-500">
          {mode === "signup"
            ? "Create an account to keep your cart saved across devices and get personalized picks."
            : "Log in to restore your saved cart and order updates."}
        </p>
        {error && (
          <p data-testid="customer-auth-error" className="mt-4 rounded-lg border border-rose-400/30 bg-rose-400/10 px-3 py-2 text-sm text-rose-300">
            {error}
          </p>
        )}
        <form onSubmit={submit} className="mt-5 space-y-3">
          {mode === "signup" && (
            <input data-testid="customer-name-input" className="input-dark" placeholder="Full name" value={form.name} onChange={set("name")} required />
          )}
          <input data-testid="customer-email-input" className="input-dark" type="email" placeholder="Email" value={form.email} onChange={set("email")} required />
          <input data-testid="customer-password-input" className="input-dark" type="password" placeholder={mode === "signup" ? "Password (min 6 characters)" : "Password"} value={form.password} onChange={set("password")} required />
          <button data-testid="customer-auth-submit" disabled={loading} className="btn-gold w-full disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : mode === "signup" ? "Create account" : "Log in"}
          </button>
        </form>
      </div>
    </div>
  );
}
